import type { RTCSession } from 'jssip/lib/RTCSession';
import type { RefObject } from 'react';
import sdpTransform from 'sdp-transform';

import { QUALITY_THRESHOLDS } from '../config';
import type { SessionInfo, SignalQuality, TKindTrack } from '../model/types';

/**
 * Синхронизирует направления трансиверов с локальным mute-состоянием и remote SDP.
 * Нужен, чтобы перед renegotiation не отправлять противоречивые direction для audio/video.
 *
 * @param info Текущее состояние SIP-сессии и сохранённые pending-направления.
 */
export const applyDirectionalPolicy = (info: SessionInfo): void => {
	const pc = info.session.connection as RTCPeerConnection | undefined;
	if (!pc) return;

	const mutedFlags = info.session.isMuted() as Record<TKindTrack, boolean>;
	const remoteDir: Partial<Record<TKindTrack, RTCRtpTransceiver['currentDirection']>> = pc
		.remoteDescription?.sdp
		? remoteAcceptsByKind(pc.remoteDescription.sdp)
		: {};
	const pendingRemoteDir = info.pendingRemoteDir ?? {};

	const applyForKind = (kind: TKindTrack) => {
		const tx = pc.getTransceivers().find(getTargetTransceiver(kind));
		const remoteDirection = pendingRemoteDir[kind] ?? remoteDir[kind] ?? null;

		const hasSenderTrack = Boolean(tx?.sender?.track && tx.sender.track.readyState !== 'ended');
		const muted = Boolean(mutedFlags[kind]) || !hasSenderTrack;

		const wantsToReceive =
			remoteDirection == null ? Boolean(pendingRemoteDir[kind]) : /send/.test(remoteDirection);
		const wantsToSend = !muted && hasSenderTrack;

		const desired: RTCRtpTransceiverDirection = wantsToReceive
			? wantsToSend
				? 'sendrecv'
				: 'recvonly'
			: wantsToSend
				? 'sendonly'
				: 'inactive';

		if (!tx) {
			if (desired === 'recvonly') {
				pc.addTransceiver(kind, { direction: desired });
			}
			return;
		}
		if (tx.direction === 'stopped') return;

		if (tx.direction !== desired) {
			tx.direction = desired;
		}
	};

	applyForKind('audio');
	applyForKind('video');
};

/** Хелпер: из сырых метрик делает 'poor'|'medium'|'good' */
export const categorizeQuality = (
	packetsAudio: number,
	lossAudio: number,
	packetsVideo: number,
	lossVideo: number,
	frameCnt: number,
	dropCnt: number,
	jitterMs: number,
	rttMs: number
): SignalQuality => {
	if (!rttMs) return 'bad';
	/* ---------- собираем все «баллы» ---------- */
	const scores: number[] = [
		check(rttMs, QUALITY_THRESHOLDS.rtt), // RTT всегда
	];

	if (packetsAudio) {
		// звук активен
		scores.push(
			check(lossAudio / packetsAudio, QUALITY_THRESHOLDS.loss),
			check(jitterMs, QUALITY_THRESHOLDS.jitter)
		);
	}

	if (packetsVideo) {
		// видео активно
		const dropRate = frameCnt + dropCnt ? dropCnt / (frameCnt + dropCnt) : 0;
		scores.push(
			check(lossVideo / packetsVideo, QUALITY_THRESHOLDS.loss),
			check(dropRate, QUALITY_THRESHOLDS.drop)
		);
	}

	/* ---------- нет ни аудио, ни видео ---------- */
	if (scores.length === 1) {
		// остался только RTT
		return scores[0] === 2 ? 'good' : scores[0] === 1 ? 'normal' : 'bad';
	}

	/* ---------- общий итог ---------- */
	const worst = Math.min(...scores);
	return worst === 2 ? 'good' : worst === 1 ? 'normal' : 'bad';
};

/**
 * Делает медиалинии audio и video «закрытыми»:
 * m=audio …  →  m=audio 0 …
 * m=video …  →  m=video 0 …
 * a=rtcp:<port> → a=rtcp:0   (в пределах той же секции)
 */
export const disableAvInSdp = (sdp: string): string => {
	const lines = sdp.split('\r\n');
	const out: string[] = [];

	let currentMedia: TKindTrack | null = null;

	for (let line of lines) {
		// m=audio / m=video  → порт = 0
		const mMatch = line.match(/^m=(audio|video)\s+(\d+)(\s+.*)$/);
		if (mMatch) {
			currentMedia = mMatch[1] as TKindTrack;
			line = `m=${currentMedia} 0${mMatch[3]}`;
			out.push(line);
			continue;
		}

		// Если внутри audio/video-секции: a=rtcp:<port>  → a=rtcp:0
		if (currentMedia && /^a=rtcp:\d+/i.test(line)) {
			line = line.replace(/^a=rtcp:\d+/i, 'a=rtcp:0');
			out.push(line);
			continue;
		}

		// Новая медиалиния начинает другую секцию
		if (line.startsWith('m=')) currentMedia = null;

		out.push(line);
	}

	return out.join('\r\n');
};

/**
 * Устанавливает «безопасные» предпочтения кодеков для уже существующих трансиверов
 * указанного RTCPeerConnection:
 * — Аудио: оставляет только Opus и PCMA.
 * — Видео: оставляет только VP8, VP9 и AV1.
 *
 * @param pc RTCPeerConnection, в котором будут обновлены трансиверы.
 * @remarks Новые трансиверы, созданные после вызова, не затрагиваются.
 */
export const ensureCodec = (pc: RTCPeerConnection) => {
	/* ---------- AUDIO ---------- */
	const audioCodecs = new Set(['audio/opus', 'audio/pcma']);
	// набор «чистых» Opus-кодеков
	const safeAudio = RTCRtpSender.getCapabilities('audio')!.codecs.filter((c) =>
		audioCodecs.has(c.mimeType.toLowerCase())
	);

	// AUDIO-трансиверы, которые уже есть
	const audioTxs = pc.getTransceivers().filter(getTargetTransceiver('audio'));

	if (audioTxs.length > 0) {
		// на всякий случай проходим по всем найденным
		audioTxs.forEach((tx) => {
			tx.setCodecPreferences(safeAudio);
		});
	}

	/* ---------- VIDEO ---------- */
	const videoCodecs = new Set(['video/vp8', 'video/vp9', 'video/av1']);
	const safeVideo = RTCRtpSender.getCapabilities('video')!.codecs.filter((c) =>
		videoCodecs.has(c.mimeType.toLowerCase())
	);

	const videoTxs = pc.getTransceivers().filter(getTargetTransceiver('video'));

	if (videoTxs.length > 0) {
		videoTxs.forEach((tx) => {
			tx.setCodecPreferences(safeVideo);
		});
	}
};

/** Извлекает из SDP секцию m=<kind> … a=<direction> */
export const getDirection = (
	sdp: string,
	kind: TKindTrack
): RTCRtpTransceiver['currentDirection'] => {
	const parsed = sdpTransform.parse(sdp); // JSON-структура
	const media = parsed.media.find((m) => m.type === kind);
	if (!media) return null; // m-линии нет вообще
	return (media.direction || 'sendrecv') as any; // по умолчаниючанию
};

/**
 * Формирует объект MediaStreamConstraints с базовыми настройками
 * для микрофона/камеры.
 *
 * @param withVideo Включать ли видео.
 * @param selectedAudioInput Идентификатор аудио-устройства (deviceId) или undefined.
 * @param selectedVideoInput Идентификатор видео-устройства (deviceId) или undefined.
 * @returns Готовые ограничения для getUserMedia.
 */
export const getMediaStreamConstraints = (
	withVideo?: boolean,
	selectedAudioInput?: string,
	selectedVideoInput?: string
): MediaStreamConstraints => ({
	audio: {
		deviceId: selectedAudioInput || undefined,
		echoCancellation: true,
		noiseSuppression: true,
		autoGainControl: true,
	},
	video: withVideo
		? {
				deviceId: selectedVideoInput || undefined,
				width: { ideal: 1280 },
				height: { ideal: 720 },
			}
		: false,
});

/**
 * Возвращает предикат для фильтрации RTCRtpTransceiver по типу дорожки.
 *
 * @param target Целевой тип дорожки ('audio' | 'video').
 * @returns Функция-предикат, совместимая с Array.prototype.filter.
 */
export const getTargetTransceiver = (target: TKindTrack) => (tx: RTCRtpTransceiver) =>
	tx.mid?.includes(target) || tx.sender.track?.kind === target || tx.receiver.track.kind === target;

/**
 * Проверяет, есть ли у переданного видеоэлемента подключенный медиа-поток с видеодорожками.
 *
 * @param ref - React RefObject, указывающий на HTMLVideoElement.
 * @returns true, если у элемента есть подключённый MediaStream с хотя бы одной видеодорожкой; иначе false.
 */
export const hasVideoTracks = (ref: RefObject<HTMLVideoElement | null>): boolean => {
	const videoEl = ref.current;
	if (!videoEl) return false;

	const stream = videoEl.srcObject as MediaStream | null;
	return stream?.getVideoTracks().some((t) => t.readyState === 'live' && t.enabled) ?? false;
};

/**
 * Проверяет, что указанная m-линия имеет порт 0.
 *
 * @param sdp   Полный текст SDP
 *
 * @returns     true  — если m-секция существует и её port == 0
 *              false — если секции нет или порт ненулевой
 */
export const isPortZero = (sdp: string): boolean => {
	let audioZero = true;
	let videoZero = true;

	for (const m of sdpTransform.parse(sdp).media) {
		if (m.type === 'audio') audioZero = m.port === 0;
		if (m.type === 'video') videoZero = m.port === 0;
	}

	return audioZero && videoZero;
};

/**
 * Определяет, отправляет ли удалённая сторона нам медиа
 * по текущему направлению трансивера.
 *
 * @param dir Текущее направление трансивера (currentDirection).
 * @returns true, если есть приём от удалённой стороны (sendrecv/recvonly или dir отсутствует); иначе false.
 */
export const isRemoteSending = (dir: RTCRtpTransceiver['currentDirection']): boolean => {
	// sendrecv  → true
	// recvonly  → true
	// sendonly  → false
	// inactive  → false
	return dir == null || /recv/.test(dir);
};

/**
 * Определяет, отправляет ли удалённая сторона нам медиа (для conference-info).
 *
 * @param dir Текущее направление медиастатуса.
 * @returns true для sendrecv/sendonly (или если dir отсутствует); иначе false.
 */
export const isRemoteSendingMedia = (dir: RTCRtpTransceiver['currentDirection']): boolean => {
	if (dir == null) return true;
	return /send/i.test(dir);
};

/**
 * Считает «балл» 0..2 для значения метрики по заданным порогам.
 *
 * @param value Значение метрики.
 * @param thresholds Пороговые значения с полями { good, medium }.
 * @returns 2 — хорошо; 1 — удовлетворительно; 0 — плохо.
 * @private
 */
const check = (value: number, { good, medium }: typeof QUALITY_THRESHOLDS.loss) =>
	value <= good ? 2 : value <= medium ? 1 : 0;

export const remoteAcceptsByKind = (
	sdp: string
): Record<TKindTrack, RTCRtpTransceiver['currentDirection']> => {
	const map: Record<string, RTCRtpTransceiver['currentDirection']> = {}; // audio|video → direction
	sdpTransform.parse(sdp).media.forEach((m) => {
		map[m.type as TKindTrack] = m.port === 0 ? 'inactive' : m.direction || 'sendrecv';
	});
	return map; // { audio:'inactive', video:'sendrecv', … }
};

/**
 * Обёртка над модификацией SDP перед отправкой/применением.
 *
 * @param sdp Исходный SDP.
 * @returns Изменённый SDP.
 */
export const sdpModifier = (sdp: string) => {
	return disableAvInSdp(sdp);
};

/**
 * Быстро включает/выключает отправку указанного трека без renegotiation.
 *
 * @param session JsSIP RTCSession.
 * @param type Тип дорожки ('audio' | 'video').
 * @param enabled Флаг активности трека.
 */
export const setTrackEnabled = (session: RTCSession, type: TKindTrack, enabled: boolean): void => {
	// Достаем RTCPeerConnection из сессии
	const pc = session.connection as RTCPeerConnection | undefined;
	if (!pc) return;

	// Переключаем все аудио-треки
	pc.getSenders().forEach((sender) => {
		if (sender.track?.kind === type) {
			sender.track.enabled = enabled;
		}
	});
};
